import { LanguageContext } from '@telia-common-components/shared';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { AnalyticsTrigger, Anchor, Card, CardContent, Grid, GridColumn, Icon, Notice, OptionCard, Separator, Text, Tooltip, TooltipToggle, Button, useTheme } from 'telia-front-react';
import { formatToTwoDecimalPlacesRoundingUp } from '../../../util/PriceUtil';
import { isMonthlyFee } from '../stores/Telia1Store';
const Telia1BenefitCard = (props) => {
    const { telia1Store, translate, index, isLoggedIn, optionProps, promotionName } = props;
    const { media } = useTheme();
    const { remainingDiscounts, price, code, isInEffect, eshopVouchers, eligibility, effectiveUntil, metadata } = props.benefit;
    const codeWithoutSuffix = code.replace('_TOT', '');
    const isMinWidthMd = useMediaQuery({ minWidth: media.width.md.min });
    const getBenefitTitleSuffix = () => {
        var _a, _b;
        if (!isInEffect || !code.includes('VAS_PnM_SAFE')) {
            return '';
        }
        if ((_a = price === null || price === void 0 ? void 0 : price.priceCode) === null || _a === void 0 ? void 0 : _a.includes('MINI')) {
            return '.mini';
        }
        if ((_b = price === null || price === void 0 ? void 0 : price.priceCode) === null || _b === void 0 ? void 0 : _b.includes('STANDARD')) {
            return '.standard';
        }
        return '';
    };
    const renderBenefitTitle = () => {
        const titleText = translate(`telia1Benefit.${codeWithoutSuffix}${getBenefitTitleSuffix()}.title`);
        const tooltip = translate(`telia1Benefit.${codeWithoutSuffix}.tooltip`);
        if (tooltip) {
            return (React.createElement(React.Fragment, null,
                React.createElement(TooltipToggle, { id: `tooltip-${code}`, className: "h5 text-bold" }, titleText),
                React.createElement(Tooltip, { referenceElement: `#tooltip-${code}` }, tooltip)));
        }
        return React.createElement("p", { className: "h5 text-bold" }, titleText);
    };
    const getCardTag = () => {
        const hideRemainingDiscounts = optionProps && telia1Store.maxInEffect(promotionName);
        const showUsedDuringLastPeriod = optionProps && !(eligibility === null || eligibility === void 0 ? void 0 : eligibility.passed) && telia1Store.anyPassed(promotionName);
        if (!isLoggedIn) {
            return false;
        }
        if (isInEffect) {
            return { text: `${translate('telia1Benefit.effective')}`, purpose: 'success' };
        }
        if (showUsedDuringLastPeriod) {
            return { text: `${translate('telia1Benefit.usedDuringLastPeriod')}`, purpose: 'text' };
        }
        if (!hideRemainingDiscounts && typeof remainingDiscounts === 'number') {
            if (remainingDiscounts === 0) {
                return { text: `${translate(telia1Store.isEmployee ? 'telia1Benefit.isEmployee' : 'telia1Benefit.noRemainingDiscounts')}`, purpose: 'attention' };
            }
            return {
                text: `${translate('telia1Benefit.remainingDiscounts', { amount: remainingDiscounts.toString() })}`,
                purpose: 'information'
            };
        }
        return true;
    };
    const getCardButtons = () => {
        const serviceContentPageAnchor = metadata['serviceContentPageUrl'] && (React.createElement(AnalyticsTrigger, { trigger: {
                id: `Click on Telia1 benefit readMore - ${code}`,
                callback: 'onClick'
            } },
            React.createElement(Anchor, { className: "text-small text-primary", target: '_blank', url: `${telia1Store.urlData.publicBaseURL}${telia1Store.urlData.getLocalizedPath(metadata['serviceContentPageUrl'])}` }, translate('telia1Benefit.readMore'))));
        if (!optionProps && !isMinWidthMd && !telia1Store.maxInEffect(promotionName) && telia1Store.isActiveTelia1Client) {
            return (React.createElement(Grid, { align: ['between-xs'] },
                React.createElement(GridColumn, { width: ['min'], align: ['middle-xs'] }, serviceContentPageAnchor),
                React.createElement(GridColumn, { width: ['min'], align: ['middle-xs'] },
                    React.createElement(Button, { text: translate(`telia1Benefit.chooseBenefits.cta${telia1Store.availableBenefitsCount(promotionName) === 1 ? '.singular' : ''}`), variant: "secondary", size: "small", iconRight: "#arrow-right", url: `${telia1Store.urlData.selfServiceURL}/teenused/telia1` }))));
        }
        return serviceContentPageAnchor;
    };
    const getCardNotice = () => {
        var _a, _b;
        if (!optionProps) {
            return;
        }
        if (effectiveUntil) {
            if ((_a = eshopVouchers[0]) === null || _a === void 0 ? void 0 : _a.redeemed) {
                return (React.createElement(Notice, { type: 'info', size: 'small' }, translate('telia1Benefit.eshopVoucher.redeemed', { date: format(new Date(eshopVouchers[0].redemptionDate), 'dd.MM.yyyy') })));
            }
            return (React.createElement(React.Fragment, null,
                ((_b = eshopVouchers[0]) === null || _b === void 0 ? void 0 : _b.code) && React.createElement("p", null, translate('telia1Benefit.eshopVoucher.code', { code: eshopVouchers[0].code })),
                React.createElement(Notice, null, translate('telia1Benefit.effectiveUntil', { date: format(new Date(effectiveUntil), 'dd.MM.yyyy') }))));
        }
        else if ((eligibility === null || eligibility === void 0 ? void 0 : eligibility.benefitAvailableFrom) && telia1Store.anyPassed(promotionName)) {
            const benefitAvailableFrom = new Date(eligibility === null || eligibility === void 0 ? void 0 : eligibility.benefitAvailableFrom);
            benefitAvailableFrom.setDate(benefitAvailableFrom.getDate() + 1);
            return React.createElement(Notice, { type: 'attention' }, translate('telia1Benefit.availableFrom', { date: format(benefitAvailableFrom, 'dd.MM.yyyy') }));
        }
        return;
    };
    const getDescription = () => {
        return (isLoggedIn && (React.createElement(React.Fragment, null,
            React.createElement("p", { className: "text-small" }, translate(`telia1Benefit.${codeWithoutSuffix}.description`)),
            getCardButtons())));
    };
    const getTermsUrl = (lang) => {
        switch (lang) {
            case 'ru':
                return metadata['termsRu'];
            case 'en':
                return metadata['termsEn'];
            default:
                return metadata['terms'];
        }
    };
    const priceTranslation = (discount) => {
        const parameters = isMonthlyFee(price)
            ? {
                period: price.period.toString(),
                price: formatToTwoDecimalPlacesRoundingUp(discount ? price.discount : price.original)
            }
            : null;
        const customTranslationKey = `telia1Benefit.${codeWithoutSuffix}.${discount ? 'price' : 'priceDescription'}`;
        const customTranslation = translate(customTranslationKey);
        const monthlyPriceTranslation = translate(`telia1Benefit.monthlyFee.${discount ? 'discount' : 'original'}`, parameters);
        return customTranslation ? translate(customTranslationKey, parameters) : monthlyPriceTranslation;
    };
    const cardContent = () => {
        return (React.createElement(CardContent, { tagTop: getCardTag() },
            React.createElement(Text, null,
                React.createElement(Grid, { gutter: "small", noWrap: true },
                    React.createElement(GridColumn, { width: ['min'], align: ['middle-xs'], className: "text-center" },
                        React.createElement(Icon, { modifiers: `${translate(`telia1Benefit.${codeWithoutSuffix}.icon`)}`, size: "md" })),
                    React.createElement(GridColumn, { width: ['max'] },
                        renderBenefitTitle(),
                        React.createElement("p", { className: "h5 text-error text-bold" }, priceTranslation(true)),
                        React.createElement("p", { className: "text-small text-light" }, priceTranslation(false)),
                        React.createElement(LanguageContext.Consumer, null, ({ lang }) => isLoggedIn &&
                            getTermsUrl(lang) && (React.createElement(React.Fragment, null,
                            React.createElement(Separator, { spacing: 'xxsmall', color: 'invisible' }),
                            React.createElement(AnalyticsTrigger, { trigger: {
                                    id: `Click on Telia1 benefit terms - ${code}`,
                                    callback: 'onClick'
                                } },
                                React.createElement(Button, { text: translate('telia1Benefit.terms'), iconLeft: "#file-pdf", link: true, element: "a", target: "_blank", url: getTermsUrl(lang), className: 'text-small text-primary' }))))))),
                getCardNotice() || getDescription())));
    };
    const renderOptionCard = () => {
        var _a, _b;
        const { invalid } = optionProps;
        return (React.createElement(OptionCard, { id: code, inputType: 'checkbox', labelText: `${translate(`telia1Benefit.${codeWithoutSuffix}.title`)}`, defaultChecked: isInEffect, disabled: remainingDiscounts === 0 ||
                !(eligibility === null || eligibility === void 0 ? void 0 : eligibility.passed) ||
                ((_a = telia1Store.disabledChildren) === null || _a === void 0 ? void 0 : _a.includes(code)) ||
                ((_b = telia1Store.disabledReplaceParents) === null || _b === void 0 ? void 0 : _b.includes(code)) ||
                (telia1Store.maxSelected(promotionName) && !telia1Store.selectedBenefitCodes.includes(code)), onChange: (checked) => telia1Store.toggleBenefit(checked, code), index: index, isInvalid: invalid }, cardContent()));
    };
    return (React.createElement(GridColumn, { key: code, width: ['xs-12', 'md-6', 'lg-4'] }, optionProps ? renderOptionCard() : React.createElement(Card, null, cardContent())));
};
export default observer(Telia1BenefitCard);
